import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';

/**
 * Panel de control de tareas: qué tiene trabajo encima y qué está colgado.
 *
 * 🔴 LO QUE SE VIENE A BUSCAR AQUÍ ES LO QUE **NO** SALE EN LA LISTA DE ABAJO.
 * La lista enseña tareas; un expediente en gestión viva al que nadie le ha
 * dejado ninguna no aparece en ningún sitio, y es justo el que se queda parado
 * meses sin que nadie lo eche de menos. Por eso la columna que manda en las dos
 * primeras tablas es «sin tarea», y por eso ordenan por ella.
 *
 * 🔴 Y por eso se cuenta DOS VECES: sobre todos los expedientes y solo sobre
 * los de gestión viva. Sin la segunda mitad la primera no dice nada por
 * cliente: un cliente con nueve mil expedientes cobrados y fallidos sale con
 * nueve mil «sin tarea», que es lo correcto y lo esperado.
 *
 * Las otras dos tablas cuentan TAREAS, no expedientes, y separan las vencidas:
 * cien tareas con fecha futura son trabajo planificado y cien vencidas son
 * trabajo parado, y sin separarlas se ven igual.
 */

const num = n => (n || 0).toLocaleString('es-ES');

const diasDesde = fecha => {
  if (!fecha) return null;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / 86400000);
};

const Th = ({ children, derecha = false }) => (
  <th
    className={`px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-400 ${
      derecha ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

// Las clases van literales: Tailwind purga lo que no encuentre escrito tal cual.
const Num = ({ children, alerta = false, apagada = false }) => (
  <td
    className={`px-3 py-2 text-right text-sm tabular-nums ${
      alerta ? 'font-semibold text-red-600' : apagada ? 'text-slate-300' : 'text-slate-600'
    }`}
  >
    {children}
  </td>
);

const Tabla = ({ titulo, explica, children }) => (
  <div className='rounded-lg border border-slate-200'>
    <div className='px-4 py-3 border-b border-slate-100'>
      <h3 className='text-sm font-semibold text-slate-800'>{titulo}</h3>
      <p className='text-xs text-slate-500 mt-0.5'>{explica}</p>
    </div>
    <div className='overflow-x-auto'>
      <table className='w-full'>{children}</table>
    </div>
  </div>
);

/** Expedientes con tarea y sin ella. Sirve igual por estado que por cliente. */
const TablaExpedientes = ({ titulo, explica, filas, campo, cabecera, esVivo, enlace }) => (
  <Tabla titulo={titulo} explica={explica}>
    <thead className='bg-slate-50'>
      <tr>
        <Th>{cabecera}</Th>
        <Th derecha>Expedientes</Th>
        <Th derecha>Sin tarea</Th>
        <Th derecha>En gestión</Th>
        <Th derecha>Sin tarea</Th>
      </tr>
    </thead>
    <tbody className='divide-y divide-slate-100'>
      {filas.map(f => (
        <tr key={f[campo]} className='hover:bg-slate-50'>
          <td className='px-3 py-2 text-sm text-slate-700'>
            {f.nombre || f[campo]}
            {esVivo && esVivo(f) && <span className='ml-2 text-xs text-slate-400'>en gestión</span>}
          </td>
          <Num>{num(f.total)}</Num>
          <Num apagada>{num(f.sin_tarea)}</Num>
          <Num>{num(f.vivos)}</Num>
          {/* La cifra del panel: expedientes que se están gestionando y no
              tienen a nadie encima. */}
          <Num alerta={f.vivos_sin_tarea > 0}>
            {/* Clicable: lleva a CUÁLES son, no solo cuántos. */}
            {enlace && f.vivos_sin_tarea > 0 && enlace(f) ? (
              <Link to={enlace(f)} className='underline hover:text-red-800' title='Ver cuáles son'>
                {num(f.vivos_sin_tarea)}
              </Link>
            ) : (
              num(f.vivos_sin_tarea)
            )}
          </Num>
        </tr>
      ))}
    </tbody>
  </Tabla>
);

/** Tareas vivas: cuántas hay y cuántas se han pasado de fecha. */
const TablaTareas = ({ titulo, explica, filas, campo, cabecera, etiqueta }) => (
  <Tabla titulo={titulo} explica={explica}>
    <thead className='bg-slate-50'>
      <tr>
        <Th>{cabecera}</Th>
        <Th derecha>Tareas vivas</Th>
        <Th derecha>Vencidas</Th>
        <Th derecha>La más vieja</Th>
      </tr>
    </thead>
    <tbody className='divide-y divide-slate-100'>
      {filas.map(f => {
        const d = diasDesde(f.mas_antigua);
        return (
          <tr key={f[campo]} className='hover:bg-slate-50'>
            <td className='px-3 py-2 text-sm text-slate-700'>
              {etiqueta ? etiqueta(f) : f.nombre || f[campo]}
            </td>
            <Num>{num(f.total)}</Num>
            <Num alerta={f.vencidas > 0}>{num(f.vencidas)}</Num>
            <Num apagada={d == null || d < 30}>{d == null ? '—' : `${num(d)} d`}</Num>
          </tr>
        );
      })}
    </tbody>
  </Tabla>
);

/**
 * La lista «Expedientes sin tarea» filtrada para que dé la misma cifra que la
 * celda: el panel cuenta todos los tipos de expediente, así que va con
 * type=all (la lista, por defecto, solo enseña extrajudicial).
 */
const enlaceSinTarea = filtros => `/assignments?${new URLSearchParams({ view: 'without-task', type: 'all', ...filtros })}`;

const KpiDeTareas = () => {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);

  // Se pide al abrir, y una sola vez. Cruza las dos colecciones enteras: no es
  // una consulta que deba correr en cada visita a la pantalla de tareas.
  useEffect(() => {
    if (!abierto || datos || cargando) return;
    setCargando(true);
    api
      .get('/assignments/kpis')
      .then(({ data }) => {
        setDatos(data.data);
        setError(null);
      })
      .catch(err => setError(getErrorMessage(err)))
      .finally(() => setCargando(false));
  }, [abierto, datos, cargando]);

  const vivos = datos?.estados_vivos || [];

  return (
    <div className='mb-6 rounded-lg border border-slate-200'>
      <button
        type='button'
        onClick={() => setAbierto(v => !v)}
        className='w-full flex items-center gap-2 px-4 py-3 text-left'
      >
        {abierto ? (
          <ChevronDownIcon className='h-4 w-4 text-slate-400' />
        ) : (
          <ChevronRightIcon className='h-4 w-4 text-slate-400' />
        )}
        <span className='text-sm font-semibold text-slate-800'>Panel de control</span>
        <span className='text-xs text-slate-500'>
          Qué está colgado: expedientes sin tarea, y tareas vencidas por cliente, tipo y persona
        </span>
      </button>

      {abierto && (
        <div className='px-4 pb-4'>
          {cargando && <p className='text-sm text-slate-500 py-4'>Cargando el panel…</p>}
          {error && <p className='text-sm text-red-700 py-4'>{error}</p>}
          {datos && (
            <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
              <TablaExpedientes
                titulo='Por estado del expediente'
                explica='«En gestión» son los estados en que el expediente se está trabajando.'
                filas={datos.por_estado}
                campo='estado'
                cabecera='Estado'
                esVivo={f => vivos.includes(f.estado)}
                enlace={f => (vivos.includes(f.estado) ? enlaceSinTarea({ estado: f.estado }) : null)}
              />
              <TablaExpedientes
                titulo='Por cliente'
                explica='Ordenado por lo que está en gestión y no tiene ninguna tarea.'
                filas={datos.por_cliente}
                campo='cliente'
                cabecera='Cliente'
                enlace={f => (/^[0-9a-f]{24}$/.test(f.cliente) ? enlaceSinTarea({ client_id: f.cliente }) : null)}
              />
              <TablaTareas
                titulo='Por tipo de tarea'
                explica='Tareas pendientes o en curso. Vencidas son las que ya pasaron de fecha.'
                filas={datos.por_tipo}
                campo='task_type'
                cabecera='Tipo'
                etiqueta={f => TASK_TYPE_LABELS[f.task_type] || f.task_type}
              />
              {/* Va pegada a «Por cliente» a propósito: las dos hablan del
                  mismo cliente y de dos averías distintas —lo que nadie ha
                  empezado y lo que se quedó a medias—. */}
              <TablaTareas
                titulo='Tareas vencidas por cliente'
                explica='De quién es el trabajo parado. Vencidas son las que ya pasaron de fecha.'
                filas={datos.tareas_por_cliente || []}
                campo='cliente'
                cabecera='Cliente'
              />
              <TablaTareas
                titulo='Por persona'
                explica='Quién tiene la pila encima, el agente de IA incluido.'
                filas={datos.por_persona}
                campo='quien'
                cabecera='Asignada a'
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KpiDeTareas;
